import { TFunnel, TFunnelCampaign } from "@/models/funnels.model";
import { TDashboardWidget, TTableColumn } from "@/models/widgets.model";
import { createFunnelService, deleteFunnelService, getFunnelById, getFunnelCampaigns, getFunnels, getFunnelWidgetData, updateFunnelService, updateTableWidgetColumns } from "@/services/funnels.service";
import { getDefaultRange } from "@/utils/dates";
import { getErrorFromBackend } from "@/utils/errors";
import { enqueueSnackbar } from "notistack";
import { useState, useCallback } from "react";
import { DateRangeType } from "react-tailwindcss-datepicker";

export type UseFunnelState = {
    funnels: TFunnel[];
    loadingFunnels: boolean;
    widgets: TDashboardWidget[];
    loadingWidgets: boolean;
    activeFunnelId: number;
    campaigns: TFunnelCampaign[];
    range: DateRangeType;
    showAddFunnelModal: boolean;
    selectedChannels: TFunnelCampaign['platform'][];
    showAddWidgetSidebar: boolean;
    funnelToEdit?: TFunnel;
}

export type UseFunnelActions = {
    loadFunnels: () => Promise<void>;
    loadFunnelWidgets: (funnelId: number) => Promise<void>;
    loadCampaigns: () => Promise<void>;
    setActiveFunnelId: React.Dispatch<React.SetStateAction<number>>
    setRange: React.Dispatch<React.SetStateAction<DateRangeType>>
    setShowAddFunnelModal: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedChannels: React.Dispatch<React.SetStateAction<TFunnelCampaign['platform'][]>>
    createFunnel: ({ name, campaigns, tags }: { name: string, campaigns?: TFunnelCampaign[], tags?: string[] }) => Promise<void>;
    updateFunnel: ({ funnelId, name, campaigns, tags }: { funnelId: number, name: string, campaigns?: TFunnelCampaign[], tags?: string[] }) => Promise<void>;
    setShowAddWidgetSidebar: React.Dispatch<React.SetStateAction<boolean>>
    onTableWidgetColumnsChange: (widgetId: number, newConfig: TTableColumn[]) => Promise<void>;
    deleteFunnel: (funnelId: number) => Promise<void>;
    setFunnelToEdit: React.Dispatch<React.SetStateAction<TFunnel | undefined>>
}

export type UseFunnelReturn = [UseFunnelState, UseFunnelActions];

const defaultRange = getDefaultRange();
const useFunnels = (): UseFunnelReturn => {

    const [funnels, setFunnels] = useState<TFunnel[]>([]);
    const [loadingFunnels, setLoadingFunnels] = useState(false);
    const [widgets, setWidgets] = useState<TDashboardWidget[]>([]);
    const [loadingWidgets, setLoadingWidgets] = useState(false);
    const [activeFunnelId, setActiveFunnelId] = useState(0);
    const [campaigns, setCampaigns] = useState<TFunnelCampaign[]>([]);
    const [range, setRange] = useState<DateRangeType>(defaultRange);
    const [showAddFunnelModal, setShowAddFunnelModal] = useState(false);
    const [selectedChannels, setSelectedChannels] = useState<TFunnelCampaign['platform'][]>([]);
    const [showAddWidgetSidebar, setShowAddWidgetSidebar] = useState(false);
    const [funnelToEdit, setFunnelToEdit] = useState<TFunnel>();

    const loadFunnels = useCallback(async () => {
        try {
            setLoadingFunnels(true);
            const response = await getFunnels();
            setFunnels(response);
            if (response.length) setActiveFunnelId(response[0].id);
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        } finally {
            setLoadingFunnels(false);
        }
    }, []);

    const loadFunnelWidgets = useCallback(async (funnelId: number) => {
        try {
            setLoadingWidgets(true);
            const response = await getFunnelById(funnelId);
            setWidgets(response);
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        } finally {
            setLoadingWidgets(false);
        }
    }, []);

    const loadCampaigns = useCallback(async () => {
        try {
            const response = await getFunnelCampaigns();
            setCampaigns(response);
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        }
    }, []);

    const createFunnel = useCallback(async ({ name, campaigns, tags }: { name: string, campaigns?: TFunnelCampaign[], tags?: string[] }) => {
        try {
            const response = await createFunnelService({ name, campaigns, tags });
            setFunnels((prevFunnels) => [...prevFunnels, response]);
            setShowAddFunnelModal(false);
            enqueueSnackbar("Funnel created successfully", { variant: "success" });
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        }
    }, []);

    const updateFunnel = useCallback(async ({ funnelId, name, campaigns, tags }: { funnelId: number, name: string, campaigns?: TFunnelCampaign[], tags?: string[] }) => {
        try {
            await updateFunnelService({ funnelId, name, campaigns, tags });
            setFunnels((prevFunnels) => prevFunnels.map(f => f.id === funnelId ? { ...f, name, campaigns: campaigns || [], campaignSubstrings: tags || [] } : f));
            setFunnelToEdit(undefined);
            setShowAddFunnelModal(false);
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        }
    }, []);

    const onTableWidgetColumnsChange = useCallback(async (widgetId: number, newConfig: TTableColumn[]) => {
        try {
            // set new config on backend
            await updateTableWidgetColumns({ funnelId: activeFunnelId, widgetId, columns: newConfig });
            // get updated widget from backend
            const updatedWidget = await getFunnelWidgetData({ funnelId: activeFunnelId, widgetId });
            setWidgets((prevWidgets) => {
                const widgetIndex = prevWidgets.findIndex(w => w.id === widgetId);
                if (widgetIndex === -1) return prevWidgets;
                const newWidgets = structuredClone(prevWidgets);
                newWidgets[widgetIndex] = updatedWidget;
                return newWidgets;
            });
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        }
    }, [activeFunnelId]);


    const deleteFunnel = useCallback(async (funnelId: number) => {
        try {
            await deleteFunnelService(funnelId);
            setFunnels((prevFunnels) => {
                if (prevFunnels.length === 1) return prevFunnels; // do not delete the last funnel
                const newFunnelList = prevFunnels.filter(f => f.id !== funnelId);
                if (activeFunnelId === funnelId && newFunnelList.length > 0) {
                    setActiveFunnelId(newFunnelList[0].id);
                }
                return newFunnelList;
            });
        } catch (e) {
            enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
        }
    }, [activeFunnelId]);


    return [
        { funnels, loadingFunnels, widgets, loadingWidgets, activeFunnelId, campaigns, range, showAddFunnelModal, selectedChannels, showAddWidgetSidebar, funnelToEdit },
        { loadFunnels, loadFunnelWidgets, loadCampaigns, setActiveFunnelId, setRange, setShowAddFunnelModal, setSelectedChannels, createFunnel, setShowAddWidgetSidebar, onTableWidgetColumnsChange, deleteFunnel, setFunnelToEdit, updateFunnel }
    ]
}

export default useFunnels
